import { normalizeText } from "@/lib/fuzzy";

type Category = { slug: string; name: string; count: number };

/** Kategorija kakvu vidi komercijalista: jedan naziv, makar ga više shopova vodilo pod
 *  različitim slugovima („Dječija obuća" je u jednom `djecija-obuca`, u drugom
 *  `djecija-obuca-2`). Filter i dalje radi nad slugovima — grupa ih samo nosi zajedno. */
export type CategoryGroup = { key: string; name: string; slugs: string[]; count: number };

export function groupCategories(categories: Category[]): CategoryGroup[] {
  const groups = new Map<string, CategoryGroup>();
  for (const c of categories) {
    const name = c.name.trim();
    const key = normalizeText(name);
    const g = groups.get(key);
    if (g) {
      g.slugs.push(c.slug);
      g.count += c.count;
    } else {
      groups.set(key, { key, name, slugs: [c.slug], count: c.count });
    }
  }
  return [...groups.values()].sort((a, b) => {
    const ka = sortKey(a.name);
    const kb = sortKey(b.name);
    return ka < kb ? -1 : ka > kb ? 1 : 0;
  });
}

/** Ključ za bosansku abecedu bez oslanjanja na `Intl`: preglednici bez bs podataka (i neki
 *  tableti) slažu Č među C, pa bi se abecedni odjeljci raspali. Naše slovo ide iza svog
 *  osnovnog — „~" je u ASCII-ju iza svih slova — pa je redoslijed C, Č, Ć, D, Dž, Đ. */
const LETTER_KEY: Record<string, string> = { "č": "c~1", "ć": "c~2", "đ": "d~2", "š": "s~", "ž": "z~" };
function sortKey(name: string): string {
  return name
    .toLocaleLowerCase("bs")
    .replace(/dž/g, "d~1")
    .replace(/[čćđšž]/g, (ch) => LETTER_KEY[ch] ?? ch);
}

export function isGroupSelected(group: CategoryGroup, selected: string[]): boolean {
  return group.slugs.every((s) => selected.includes(s));
}

export function toggleGroup(group: CategoryGroup, selected: string[]): string[] {
  if (isGroupSelected(group, selected)) return selected.filter((s) => !group.slugs.includes(s));
  return [...selected, ...group.slugs.filter((s) => !selected.includes(s))];
}

export function filterGroups(groups: CategoryGroup[], query: string): CategoryGroup[] {
  const q = normalizeText(query);
  if (!q) return groups;
  return groups.filter((g) => g.key.includes(q));
}

/** Odjeljci po početnom slovu, redom kojim grupe već stoje. Naša slova (Č, Š, Ž) ostaju
 *  svoja — komercijalista ih traži tamo gdje ih abeceda stavlja, ne pod C/S/Z. */
export function byInitial(groups: CategoryGroup[]): [string, CategoryGroup[]][] {
  // Map, a ne „nastavi zadnji odjeljak": slovo je i React ključ, pa se ne smije javiti dvaput
  // ni kad grupe stignu u neočekivanom redu.
  const sections = new Map<string, CategoryGroup[]>();
  for (const g of groups) {
    const first = g.name.charAt(0).toLocaleUpperCase("bs");
    const letter = /\p{L}/u.test(first) ? first : "#";
    const list = sections.get(letter);
    if (list) list.push(g);
    else sections.set(letter, [g]);
  }
  return [...sections];
}
